import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';

import makeArray from 'webapp/helpers/make-array';
import didInsert from 'webapp/modifiers/did-insert';
import onWindowEvents from 'webapp/modifiers/on-window-events';
import BarLoaderComponent from 'webapp/components/bar-loader';
import TemplatesGridItemComponent from 'webapp/components/templates/grid-item';

import type Template from 'webapp/models-custom/template';
import type BoardRecord from 'webapp/models/board';
import type BoardLoopRecord from 'webapp/models/board-loop';

export interface Args {
	isHidden?: boolean;
	trackingOrigin?: string;
	onLoaded?: () => void;
}

export enum FlexContextSize {
	BOARD = 'board',
	SMALL_BOARD = 'small-board',
}

const DEFAULT_EXTRA_ITEMS_SHOWN = 14;

export default abstract class TemplatesPublicRecommendedBaseComponent<
	T extends Args,
> extends Component<T> {
	@service('account') declare accountService: Service.Account;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('model-manager/templates') declare mmTemplatesService: Service.ModelManager.Templates;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/board-loops')
	declare mmBoardLoopsService: Service.ModelManager.BoardLoops;

	@tracked isLoaderShown = true;

	@tracked maxItemsShown = 8;

	abstract get model(): Template[];
	abstract redirectRoute: string;
	abstract defaultOrigin: string;
	abstract requestTemplates(): Promise<unknown>;
	abstract onTemplateUse(model: Template): void;

	linkText = 'Browse more';
	isLinkShown = true;
	size = FlexContextSize.BOARD;
	typeClass = 'recommended';
	maxShownExtraItems = DEFAULT_EXTRA_ITEMS_SHOWN;
	filterHash?: string;

	get title() {
		return 'Start with one of our popular templates...';
	}

	get isShown() {
		return (
			!this.args.isHidden &&
			!this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins &&
			!this.currentUserService.isViewer
		);
	}

	get origin() {
		return this.args.trackingOrigin || this.defaultOrigin;
	}

	get numberOfRowsShown() {
		return 1;
	}

	protected onBoardTemplateUse(model: Template): void {
		const afterUse = (boardRecord: BoardRecord) => {
			const afterTransition = () => {
				const boardModel = this.mmBoardsService.createModel(boardRecord);
				this.mmBoardsService.openPreview(boardModel, null);
			};

			this.navigationService.transitionToRoute('boards.index').then(afterTransition);
		};

		this.trackTemplateUse(model);
		this.mmBoardsService.createBoardFromTemplate(model, this.origin, afterUse);
	}

	protected onBoardLoopTemplateUse(model: Template): void {
		const afterUse = (boardLoopRecord: BoardLoopRecord) => {
			const afterTransition = () => {
				const boardLoopModel = this.mmBoardLoopsService.findModel(boardLoopRecord.id!);
				if (!boardLoopModel) {
					return;
				}

				this.mmBoardLoopsService.openPreviewPopup(boardLoopModel);
			};

			this.navigationService.transitionToRoute('boards.loops.index').then(afterTransition);
		};

		this.trackTemplateUse(model);
		this.mmBoardLoopsService.createBoardLoopFromTemplate(model, this.origin, afterUse);
	}

	private trackTemplateUse(model: Template): void {
		this.mmTemplatesService.trackEvent(model, TrackingEvent.USE_A_TEMPLATE);
	}

	@action
	setupView(node: HTMLElement) {
		void this.requestTemplates().then(() => {
			this.isLoaderShown = false;

			const onLoaded = this.args.onLoaded;
			if (onLoaded) {
				onLoaded();
			}
			next(this, this.onScreenResize, null, node);
		});
	}

	@action
	previewTemplate(model: Template): void {
		this.mmTemplatesService.openPreviewPopup(model, () => {
			this.useTemplate(model);
		});
	}

	@action
	useTemplate(model: Template): void {
		this.onTemplateUse(model);
	}

	@action
	browseMore() {
		this.navigationService.transitionToRoute(this.redirectRoute).then(() => {
			if (this.filterHash) {
				window.location.hash = this.filterHash;
			}
		});
	}

	@action
	onScreenResize(_event: Event | null, node: HTMLElement) {
		const numberOfRowsShown = this.numberOfRowsShown,
			containerNode = node.querySelector('ul'),
			containerWidth = containerNode?.offsetWidth || 0,
			gridItemWidth = containerNode?.querySelector('li')?.offsetWidth || 0;

		if (containerWidth) {
			const numberOfCardInRow = Math.floor(containerWidth / gridItemWidth);
			this.maxItemsShown = numberOfRowsShown * numberOfCardInRow;
		}
	}
	<template>
		{{#if this.isShown}}
			<div
				class="empty-screen-{{this.typeClass}}"
				{{didInsert this.setupView}}
				{{onWindowEvents this.onScreenResize "resize" 200}}
			>
				<div class="empty-screen-{{this.typeClass}}__header">
					<h3>{{this.title}}</h3>
					{{#if this.isLinkShown}}
						<a role="button" {{on "click" this.browseMore}}>{{this.linkText}}</a>
					{{/if}}
				</div>
				<div class="empty-screen-{{this.typeClass}}__content">
					{{#if this.isLoaderShown}}
						<BarLoaderComponent />
					{{else}}
						<ul class="flex-ct flex-ct--size-{{this.size}}">
							{{#each this.model as |templateModel|}}
								<TemplatesGridItemComponent
									@model={{templateModel}}
									@onPreviewTemplate={{this.previewTemplate}}
									@onUseTemplate={{this.useTemplate}}
									@isVisibleOnScreen={{true}}
								/>
							{{/each}}

							{{! adding (max-in-line - 1) fake items so the incomplete last row items are not stretched too much }}
							{{#each (makeArray length=this.maxShownExtraItems)}}
								<li class="flex-item flex-item--fake"></li>
							{{/each}}
						</ul>
					{{/if}}
				</div>
			</div>
		{{/if}}
	</template>
}
